import { useQuery } from "@tanstack/react-query";
import type { QueryKey, UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { TaskOptionsData, TaskOptionsResponse } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";

export const GAME_PASS_TASK_OPTIONS_QUERY_KEY = [
  "game-pass",
  "task-options",
] as const;

/**
 * Fetch the Create Task / Create Challenge dropdown catalogue from
 * `GET /game-pass-admin/v2/admin/task-options`.
 *
 * Returns just the `data` payload so consumers read `sports` / `bet_types` /
 * … directly. A missing / empty `data` resolves to `{}` rather than throwing:
 * every consumer derives its options with a static fallback, so an absent
 * catalogue degrades to the bundled defaults instead of erroring the page.
 */
export const fetchTaskOptions = async (): Promise<TaskOptionsData> => {
  const response = await getPrivateInstance().get<TaskOptionsResponse>(
    API_ROUTES.GET_GAME_PASS_TASK_OPTIONS,
  );
  return response.data?.data ?? {};
};

export const getTaskOptionsQueryOptions = (): {
  queryKey: QueryKey;
  queryFn: typeof fetchTaskOptions;
  staleTime: number;
  retry: typeof retryOnTransientErrors;
} => ({
  queryKey: [...GAME_PASS_TASK_OPTIONS_QUERY_KEY],
  // The catalogue changes rarely, so cache it for a few minutes to avoid a
  // refetch every time the admin opens a task / challenge editor.
  staleTime: 1000 * 60 * 5,
  queryFn: fetchTaskOptions,
  retry: retryOnTransientErrors,
});

export const useGetTaskOptions = (): UseQueryResult<TaskOptionsData, Error> =>
  useQuery<TaskOptionsData, Error>({
    ...getTaskOptionsQueryOptions(),
    refetchOnWindowFocus: false,
  });
